import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useDispatch } from "react-redux";
import Comment from "components/input/Comment.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { RejectProps, RejectActionData } from "types/modals";
import { getTaskUserDescription } from "utils/valueFormatter.function";
import { rejectTask } from "components/modals/modal.actions";
import { Notification, NotificationType } from "components/common/Notification.component";
import { launchDarkly } from "utils/launchDarkly";

const Reject = ({ more, closeModal, loopFocus, errorCallback }: RejectProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const getTaskByUID = launchDarkly.companyClient?.variation("fe-get-task-by-uid", false);
    const commentInputRef = useRef<HTMLTextAreaElement>(null);
    const [comment, setComment] = useState("");
    const [actionRunning, setActionRunning] = useState(false);
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);

    useEffect(() => {
        commentInputRef.current?.focus();
    }, []);

    const handleSuccess = useCallback(() => {
        more.handledTaskCallback(more?.tasks[0]?.key, more.navigate);
        closeModal();
    }, [more, closeModal]);

    const handleError = useCallback(
        (error: Record<string, any>) => {
            setDisplayError(error.error ?? error);
            setActionRunning(false);
        },
        [errorCallback],
    );

    const handleReject = useCallback(() => {
        if (!comment) return;

        const currentTask = more?.tasks[0];
        if (!currentTask) return;

        setActionRunning(true);
        const data: RejectActionData = {
            key: currentTask.key,
            ...(getTaskByUID && currentTask.uid ? { uid: currentTask.uid } : {}),
            comment,
            taskDescription: getTaskUserDescription(currentTask),
        };

        dispatch(rejectTask(data, handleSuccess, handleError));
    }, [comment, more, dispatch, handleSuccess, handleError, getTaskByUID]);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setDisplayError(null);
    }, []);

    const okEnabled = !!(comment && !actionRunning);

    return (
        <>
            <div className="ga-modal__content">
                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}
                <Comment
                    ref={commentInputRef}
                    inputHint={translate("popUp.reject.commentHint")}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX}
                    id="comment"
                    propagateValue={handleCommentChange}
                    value={comment}
                    label={translate("popUp.reject.commentLabel")}
                    labelState={`(${translate("popUp.required")})`}
                />
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                okEnabled={okEnabled}
                okCallback={handleReject}
                okText={translate("popUp.ok")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default Reject;
